import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "../ui/button";
import { Plus, FileText, Clock, CheckCircle, ArrowClockwise, CurrencyDollar, ChartBar, Warning } from "@phosphor-icons/react";

export default function QuotationDashboard({ token, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotations/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(data);
    } catch { toast.error("Error al cargar estadisticas"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="flex justify-center py-20"><ArrowClockwise size={32} className="animate-spin text-blue-500" /></div>;

  const s = stats || {};

  return (
    <div className="space-y-6" data-testid="quotation-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Cotizaciones</h2>
          <p className="text-sm text-slate-500">Resumen general y del mes actual</p>
        </div>
        <Button onClick={() => onNavigate("create")} className="bg-blue-600 hover:bg-blue-700" data-testid="create-quotation-btn">
          <Plus size={18} className="mr-2" /> Crear Nuevo Presupuesto
        </Button>
      </div>

      {/* All-time totals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-blue-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><FileText size={22} className="text-blue-600" /></div>
            <span className="text-sm text-slate-500">Total Cotizaciones</span>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="stat-total-all">{s.total_all || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-green-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center"><CurrencyDollar size={22} className="text-green-600" /></div>
            <span className="text-sm text-slate-500">Valor Total Acumulado</span>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="stat-total-value-all">${(s.total_value_all || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</p>
        </div>
        <div className="bg-white rounded-xl border border-indigo-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center"><FileText size={22} className="text-indigo-600" /></div>
            <span className="text-sm text-slate-500">Enviados este mes</span>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="stat-total-sent">{s.total_sent || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-purple-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center"><ChartBar size={22} className="text-purple-600" /></div>
            <span className="text-sm text-slate-500">Valor cotizado (mes)</span>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="stat-total-value">${(s.total_value || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate("list", "pendiente")}>
          <Clock size={28} className="text-amber-600" />
          <div>
            <p className="text-2xl font-bold text-amber-800">{s.pending || 0}</p>
            <p className="text-xs text-amber-600">Pendientes de respuesta</p>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate("list", "aprobado")}>
          <CheckCircle size={28} className="text-green-600" />
          <div>
            <p className="text-2xl font-bold text-green-800">{s.approved_week || 0}</p>
            <p className="text-xs text-green-600">Aprobados esta semana</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate("list", "vencido")}>
          <Warning size={28} className="text-red-600" />
          <div>
            <p className="text-2xl font-bold text-red-800">{s.expiring || 0}</p>
            <p className="text-xs text-red-600">Por vencer (3 dias)</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Cotizaciones - Ultimos 6 meses</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={s.monthly || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="month" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip formatter={(v, n) => [v, n === "total" ? "Total" : "Aprobados"]} />
            <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Total" />
            <Bar dataKey="approved" fill="#22C55E" radius={[4, 4, 0, 0]} name="Aprobados" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
